import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import { useAuth } from '../context/AuthContext';

const AcceptInvitation = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    const { refreshUser } = useAuth();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [invitationData, setInvitationData] = useState(null);
    const [loadingInvitation, setLoadingInvitation] = useState(true);

    useEffect(() => {
        if (!token) {
            setError('Invalid invitation link');
            setLoadingInvitation(false);
            return;
        }

        // Fetch invitation details
        const fetchInvitation = async () => {
            const result = await authService.getInvitation(token);
            if (result.ok) {
                setInvitationData(result.data);
            } else {
                setError('Invalid or expired invitation link');
            }
            setLoadingInvitation(false);
        };

        fetchInvitation();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);

        try {
            const result = await authService.acceptInvitation(token, password);

            if (result.ok) {
                // Refresh user data and redirect to customer dashboard
                await refreshUser();
                navigate('/customer-dashboard');
            } else if (result.status === 400) {
                setError('Invalid or expired invitation');
            } else {
                setError('Failed to create account. Please try again.');
            }
        } catch (err) {
            setError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loadingInvitation) {
        return (
            <div className="bg-dark text-light d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p>Loading invitation...</p>
                </div>
            </div>
        );
    }

    if (!invitationData) {
        return (
            <div className="bg-dark text-light d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-md-6">
                            <div className="card bg-secondary-subtle border-danger">
                                <div className="card-body text-center py-5">
                                    <i className="bi bi-exclamation-triangle text-danger" style={{ fontSize: '3rem' }}></i>
                                    <h3 className="mt-3 mb-3">Invalid Invitation</h3>
                                    <p className="text-secondary">{error}</p>
                                    <a href="/login.html" className="btn btn-primary mt-3">
                                        Go to Login
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-dark text-light d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-md-6">
                        <div className="text-center mb-4">
                            <h1 className="display-4 mb-3">
                                <i className="bi bi-shield-check text-primary me-3"></i>
                                frwd labs
                            </h1>
                            <p className="text-secondary">Create Your Account</p>
                        </div>

                        <div className="card bg-secondary-subtle border-secondary">
                            <div className="card-body p-4">
                                <div className="mb-4">
                                    <h4 className="mb-3">Welcome!</h4>
                                    <p className="text-secondary mb-2">
                                        You've been invited to join frwd labs.
                                    </p>
                                    <div className="bg-dark p-3 rounded">
                                        <p className="mb-2">
                                            <strong>Email:</strong> {invitationData.email}
                                        </p>
                                        {invitationData.org && (
                                            <p className="mb-0">
                                                <strong>Organization:</strong> {invitationData.org}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {error && (
                                    <div className="alert alert-danger" role="alert">
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        {error}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label htmlFor="password" className="form-label">
                                            Create Password <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            id="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            disabled={loading}
                                            minLength={8}
                                            placeholder="At least 8 characters"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label htmlFor="confirmPassword" className="form-label">
                                            Confirm Password <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            id="confirmPassword"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            disabled={loading}
                                            minLength={8}
                                            placeholder="Confirm your password"
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        className="btn btn-primary w-100"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Creating Account...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-circle me-2"></i>
                                                Create Account
                                            </>
                                        )}
                                    </button>
                                </form>

                                <div className="mt-4 text-center">
                                    <p className="text-secondary small mb-0">
                                        By creating an account, you agree to our Terms of Service and Privacy Policy.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AcceptInvitation;

